import type {SalesDynamicsDay} from '@ozon-ai/ozon/contracts';
import {CartesianGrid, Line, LineChart, XAxis, YAxis} from 'recharts';
import {Card, CardDescription, CardHeader, CardTitle} from '@/components/ui/card.tsx';
import {
	ChartContainer,
	ChartTooltip,
	ChartTooltipContent,
	type ChartConfig,
} from '@/components/ui/chart.tsx';
import {Empty, EmptyDescription, EmptyHeader, EmptyTitle} from '@/components/ui/empty.tsx';
import {Skeleton} from '@/components/ui/skeleton.tsx';
import {formatUnits} from '../unit-economics-page/format-money';
import {useProductPageContext} from './product-context';

const chartConfig = {
	inStock: {label: 'Остаток', color: 'var(--chart-4)'},
} satisfies ChartConfig;

export function ProductStocksPage() {
	const state = useProductPageContext();
	const days = state.salesDynamics?.statistics ?? [];
	if (!state.product?.ozonSku) {
		return (
			<Empty className="border border-border">
				<EmptyHeader>
					<EmptyTitle>Нет SKU Ozon</EmptyTitle>
					<EmptyDescription>Историю остатков для этого товара посчитать нельзя</EmptyDescription>
				</EmptyHeader>
			</Empty>
		);
	}
	const known = days.filter((day) => day.inStock != null);
	const inStockDays = known.filter((day) => (day.inStock ?? 0) > 0).length;
	const oosDays = known.filter((day) => day.inStock === 0).length;
	const last = [...known].reverse()[0];
	const dailyPace = pace(days);
	return (
		<section className="flex flex-col gap-4">
			<h2 className="text-base font-medium">Остатки</h2>
			{state.salesDynamicsLoading ? <Skeleton className="h-40 w-full"/> : null}
			{state.salesDynamicsError ? <p className="text-sm text-destructive">{state.salesDynamicsError}</p> : null}
			{!state.salesDynamicsLoading && known.length === 0 ? (
				<Empty className="border border-border">
					<EmptyHeader>
						<EmptyTitle>Нет данных об остатке</EmptyTitle>
						<EmptyDescription>За выбранный период история FBO не пришла</EmptyDescription>
					</EmptyHeader>
				</Empty>
			) : null}
			{known.length > 0 ? (
				<>
					<div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
						<StockStat label="Текущий остаток" value={last?.inStock == null ? '—' : formatUnits(last.inStock)}/>
						<StockStat label="Дни наличия" value={String(inStockDays)}/>
						<StockStat label="Дни без остатка" value={String(oosDays)}/>
						<StockStat
							label="Покрытие"
							value={last?.inStock == null || dailyPace == null || dailyPace <= 0 ? '—' : `${formatNumber(last.inStock / dailyPace)} дн.`}
						/>
					</div>
					<p className="text-sm text-muted-foreground">
						Темп в дни наличия: {dailyPace == null ? '—' : `${formatNumber(dailyPace)} шт. в день`}.
					</p>
					<ChartContainer config={chartConfig} className="aspect-auto h-64 w-full">
						<LineChart data={days} margin={{left: 8, right: 8, top: 8}}>
							<CartesianGrid vertical={false}/>
							<XAxis dataKey="date" tickLine={false} axisLine={false} minTickGap={24} tickFormatter={(value: string) => String(value).slice(5)}/>
							<YAxis allowDecimals={false} tickLine={false} axisLine={false} width={40}/>
							<ChartTooltip content={<ChartTooltipContent labelFormatter={(value) => String(value)}/>}/>
							<Line type="monotone" dataKey="inStock" stroke="var(--color-inStock)" strokeWidth={2} dot={false} connectNulls={false}/>
						</LineChart>
					</ChartContainer>
				</>
			) : null}
		</section>
	);
}

function StockStat({label, value}: {label: string; value: string}) {
	return (
		<Card size="sm">
			<CardHeader>
				<CardDescription>{label}</CardDescription>
				<CardTitle className="text-xl tabular-nums">{value}</CardTitle>
			</CardHeader>
		</Card>
	);
}

function pace(days: SalesDynamicsDay[]): number | null {
	const stocked = days.filter((day) => day.inStock != null && day.inStock > 0);
	if (stocked.length === 0) return null;
	return stocked.reduce((sum, day) => sum + day.sold, 0) / stocked.length;
}

function formatNumber(value: number): string {
	return value.toLocaleString('ru-RU', {maximumFractionDigits: 1});
}
